import type { SerializedNode } from "@craftjs/core";
import type {
  Page,
  Recording,
  RecordingCategoryUnion,
  RecordingRaw,
} from "src/types";

import type { VideoBlockProps } from "@/components/editor/primitives/video/video-component";
import { VIDEO_PRIMITIVE_DISPLAY_NAME } from "@/components/editor/primitives/video/video-component";
import {
  VIDEO_GRID_DISPLAY_NAME,
  VideoGridProps,
} from "@/components/editor/shared/media/video-grid/video-grid-component";
import type { Page as ContentHubPage } from "@/content-hub/page/domain";

const getters = {
  [VIDEO_PRIMITIVE_DISPLAY_NAME]: (node: SerializedNode) => {
    const props = node.props as VideoBlockProps;
    return props.recordingId ? [props.recordingId] : [];
  },
  [VIDEO_GRID_DISPLAY_NAME]: (node: SerializedNode) => {
    const props = node.props as VideoGridProps;
    return props.videoList.reduce((videos, currentVideo) => {
      if (typeof currentVideo === "string") return videos;
      if (currentVideo.kind === "recording") {
        videos.push(currentVideo.recordingId);
      }
      return videos;
    }, []);
  },
};

export const getUniqueRecordingIdsFromPage = (page: Page | ContentHubPage) => {
  const recordingsId = Object.values(page.content).reduce(
    (accumulator, node) => {
      if (
        [VIDEO_PRIMITIVE_DISPLAY_NAME, VIDEO_GRID_DISPLAY_NAME].includes(
          node.custom.displayName
        )
      ) {
        const ids = getters[node.custom.displayName]?.(node);
        accumulator.push(...ids);

        return accumulator;
      }

      return accumulator;
    },
    []
  );

  return Array.from(new Set(recordingsId));
};

//this is a helper function to build the title of a recording from the startedAt date if title is not provided
export const buildRecordingTitle = ({
  title,
  startedAt,
}: {
  title?: string;
  startedAt: string;
}): string => {
  if (title) return title;

  try {
    const date = new Date(startedAt);
    const dateTimeFormat = new Intl.DateTimeFormat("en", {
      dateStyle: "long",
      timeStyle: "short",
      hour12: true,
    });
    return dateTimeFormat.format(date);
  } catch (err) {
    return "";
  }
};

export const mapRecordingRawToRecording = (
  recording: RecordingRaw
): Recording => ({
  id: recording.id,
  title: buildRecordingTitle({
    title: recording.title,
    startedAt: recording.started_at,
  }),
  description: recording.description,
  category: recording.category as RecordingCategoryUnion,
  duration: recording.duration,
  failure_reason: recording.failure_reason,
  eventPart: {
    name: recording.event_part_name,
    type: recording.event_part_type,
    id: recording.event_part_id,
  },
  playableUrl: recording.playable_url,
  downloadUrl: recording.download_url,
  thumbnail: recording.thumbnail,
  published: recording.published,
  startedAt: recording.started_at,
  endedAt: recording.ended_at,
  parentRecordingId: recording.parent_recording_id,
  status: recording.status,
  statusChangedAt: recording.status_changed_at,
  simulives: recording.simulives,
  isOriginal: recording.is_original,
  createdAt: recording.created_at,
  aspectRatio: recording.aspect_ratio,
});
